// Stale-bundle banner events (see components/UpdateBanner.jsx).
//
// The question these answer: how many real sessions are running a bundle old
// enough that we told them about it, and how many act on the offer. A high
// prompted count with a low reloaded count means the banner isn't convincing;
// a high prompted count on its own means tabs are living much longer than
// assumed, which is the thing worth knowing.
//
// running/served basenames are carried so the AGE of the stale bundle is
// recoverable after the fact — a `running` hash that shows up months after it
// stopped being deployed is a tab that was never once closed.
//
// Gating is inherited: analyticsGate.js stubs posthog.capture entirely when
// analytics is suppressed, so no host or env checks belong here.

import posthog from "posthog-js";

// Never let analytics break the banner — swallow capture failures.
function safeCapture(event, properties, options) {
  try {
    posthog.capture(event, properties, options);
  } catch {
    /* ignore */
  }
}

// Staleness was DETECTED. Deliberately ignores `suppressed` and visibility —
// this is cohort size ("how many people are running stale code"), not the
// funnel. A stale user who starts a draft and abandons it is suppressed for the
// whole session and never prompted, so prompted alone undercounts the problem.
//
// detected ≥ prompted always. The gap between them is people who were stale but
// mid-draft the entire time they were here.
export function captureUpdateDetected({ running, served }) {
  safeCapture("app_update_detected", { running, served });
}

// Banner became VISIBLE — i.e. someone was actually offered a reload. This is
// the funnel numerator against app_update_reloaded; keep it visibility-gated,
// not detection-gated, or the conversion rate stops meaning anything.
//
// The caller guards repeats: a dismiss, or a suppression lifting when a draft
// ends, is the same prompt continuing — not a second one — so this fires at
// most once per page load.
export function captureUpdatePrompted({ running, served }) {
  safeCapture("app_update_prompted", { running, served });
}

// Reload clicked. This fires immediately before location.reload(), so a queued
// XHR would be killed by the navigation — sendBeacon + send_instantly is what
// makes it survive. Without it the conversion half of the funnel would read as
// near-zero for reasons that have nothing to do with the banner.
export function captureUpdateReloaded({ running, served }) {
  safeCapture(
    "app_update_reloaded",
    { running, served },
    { transport: "sendBeacon", send_instantly: true }
  );
}
